export const profileTabIds = [
  'profile',
  'previous_classes',
  'connections',
  'schedules',
  'courses',
  'degree_plans',
  'billing',
  'sms',
  'professional',
  'memories',
] as const;

export type ProfileTabId = (typeof profileTabIds)[number];
export type ProfileViewState = 'loading' | 'populated' | 'partial' | 'error';

export type ProfileMetric = {
  label: string;
  value: string;
  detail: string;
  tone: 'neutral' | 'positive' | 'warning' | 'critical';
};

export type ProfileConnectionSummary = {
  acceptedCount: number;
  incomingRequestCount: number;
  sentRequestCount: number;
};

type ProfileFriendshipRecord = {
  friendship?: { status?: string | null } | null;
  is_sent_by_user?: boolean | null;
};

/** Serializable account-settings state for route-owned renderers and fixtures. */
export type ProfileViewModel = {
  state: ProfileViewState;
  activeTab: ProfileTabId;
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  sourceUpdatedAt: string | null;
  sourceStale: boolean;
  error: string | null;
  metrics: ProfileMetric[];
  completedCourseCount: number;
  memoryCount: number;
};

export function isProfileTabId(value: string | null): value is ProfileTabId {
  return profileTabIds.includes(value as ProfileTabId);
}

export function createProfileTabUrl(pathname: string, search: string, tab: ProfileTabId): string {
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return `${pathname}?${params.toString()}`;
}

export function summarizeProfileFriendships(records: ProfileFriendshipRecord[]): ProfileConnectionSummary {
  return records.reduce<ProfileConnectionSummary>((summary, record) => {
    const status = (record.friendship?.status || '').trim().toUpperCase();
    if (status === 'ACCEPTED') summary.acceptedCount += 1;
    if (status === 'SENT' || status === 'PENDING') {
      if (record.is_sent_by_user) summary.sentRequestCount += 1;
      else summary.incomingRequestCount += 1;
    }
    return summary;
  }, { acceptedCount: 0, incomingRequestCount: 0, sentRequestCount: 0 });
}

export function validateProfileAcademicData(input: { year: string; majors: string[]; school: string }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!input.year) errors.year = 'Please select your year';
  if (!input.majors.length) errors.majors = 'Please select at least one major';
  if (!input.school) errors.school = 'Please select your school';
  return errors;
}

export function filterUniqueProfileCourses(courses: RutgersCourse[], rawQuery: string): RutgersCourse[] {
  const query = rawQuery.trim().toLowerCase();
  const matches = query ? courses.filter((course) => {
    if (query.includes(':')) {
      const [department, classNumber] = query.split(':').map((part) => part.trim());
      if (department && classNumber && course.department.toLowerCase().includes(department) && course.class_number.toLowerCase().includes(classNumber)) return true;
    }
    return course.courseName.toLowerCase().includes(query) || course.department.toLowerCase().includes(query) || course.class_number.toLowerCase().includes(query);
  }) : courses;
  const unique = new Map<string, RutgersCourse>();
  matches.forEach((course) => { if (!unique.has(course.baseId)) unique.set(course.baseId, course); });
  return Array.from(unique.values());
}

export function selectProfileMetrics(input: {
  savedScheduleCount: number;
  courseListCount: number;
  degreePlanCount: number | null;
  friendsCount: number | null;
}): ProfileMetric[] {
  return [
    { label: 'Schedules', value: String(input.savedScheduleCount), detail: 'Saved scheduling options', tone: 'neutral' },
    { label: 'Course lists', value: String(input.courseListCount), detail: 'Saved course collections', tone: 'neutral' },
    { label: 'Degree plans', value: input.degreePlanCount == null ? 'Unavailable' : String(input.degreePlanCount), detail: input.degreePlanCount == null ? 'Plan service is unavailable' : 'Planning roadmaps', tone: input.degreePlanCount == null ? 'warning' : 'positive' },
    { label: 'Friends', value: input.friendsCount == null ? 'Unavailable' : String(input.friendsCount), detail: input.friendsCount == null ? 'Friendship service is unavailable' : 'Accepted connections', tone: input.friendsCount == null ? 'warning' : 'neutral' },
  ];
}

export function createProfileViewModel(input: Omit<ProfileViewModel, 'metrics'> & {
  metricInput: Parameters<typeof selectProfileMetrics>[0];
}): ProfileViewModel {
  return { ...input, metrics: selectProfileMetrics(input.metricInput) };
}
import type { RutgersCourse } from '@/app/types/interfaces';
